import { cloneElement, isValidElement, useId, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '@/lib/format';
import { tabId, tabPanelId } from './tabIds';
import type { ActorRef, ApprovalStatus } from '@/lib/api/types';

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg className={cx('animate-spin text-fg-muted', className)} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Badge({ children, tone = 'neutral', className }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info'; className?: string }) {
  const tones = {
    neutral: 'bg-bg-sunken text-fg-muted',
    accent: 'bg-accent-soft text-accent',
    ok: 'bg-ok/15 text-ok',
    warn: 'bg-warn/15 text-warn',
    danger: 'bg-danger/15 text-danger',
    info: 'bg-info/15 text-info',
  };
  return <span className={cx('inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium leading-none', tones[tone], className)}>{children}</span>;
}

export const APPROVAL_TONE: Record<ApprovalStatus, { tone: 'neutral' | 'ok' | 'warn' | 'info'; label: string; dot: string }> = {
  none: { tone: 'neutral', label: 'No status', dot: 'bg-transparent' },
  in_progress: { tone: 'info', label: 'In progress', dot: 'bg-info' },
  needs_review: { tone: 'warn', label: 'Needs review', dot: 'bg-warn' },
  approved: { tone: 'ok', label: 'Approved', dot: 'bg-ok' },
};

export function Avatar({ actor, size = 24, className }: { actor: ActorRef | { display_name?: string | null; avatar_url?: string | null } | null | undefined; size?: number; className?: string }) {
  const name = actor?.display_name ?? ('guest_name' in (actor ?? {}) ? (actor as ActorRef).guest_name : null) ?? '?';
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0);
  if (actor?.avatar_url)
    return <img src={actor.avatar_url} alt={name} width={size} height={size} className={cx('rounded-full object-cover', className)} />;
  return (
    <span
      title={name}
      style={{ width: size, height: size, background: `hsl(${hue} 50% 45%)`, fontSize: size * 0.4 }}
      className={cx('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white', className)}
    >
      {initials}
    </span>
  );
}

export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-fg-muted">
      {icon && <div className="text-fg-subtle">{icon}</div>}
      <div className="text-sm font-medium text-fg">{title}</div>
      {body && <div className="max-w-sm text-xs">{body}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded bg-bg-sunken', className)} />;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}

/**
 * WAI-ARIA tabs: roving tabindex (only the selected tab is in the tab order), Arrow/Home/End move and
 * select, `aria-controls` when the caller renders a panel with `tabPanelId(idBase, id)`.
 */
export function Tabs<T extends string>({ value, onChange, tabs, className, idBase, label }: { value: T; onChange: (v: T) => void; tabs: Array<{ id: T; label: ReactNode }>; className?: string; idBase?: string; label?: string }) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const idx = tabs.findIndex((t) => t.id === value);
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next < 0 || !tabs[next]) return;
    e.preventDefault();
    onChange(tabs[next]!.id);
    e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={cx('flex gap-1 border-b border-line', className)} onKeyDown={onKey}>
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          type="button"
          id={idBase ? tabId(idBase, t.id) : undefined}
          aria-controls={idBase ? tabPanelId(idBase, t.id) : undefined}
          aria-selected={value === t.id}
          tabIndex={value === t.id ? 0 : -1}
          onClick={() => onChange(t.id)}
          className={cx(
            '-mb-px border-b-2 px-3 py-2 text-xs font-medium',
            value === t.id ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  const id = useId();
  // the trigger is described by the tooltip for assistive tech (a hover-only visual is not enough)
  const trigger = isValidElement<{ 'aria-describedby'?: string }>(children) ? cloneElement(children, { 'aria-describedby': id }) : children;
  return (
    <span className="group relative inline-flex">
      {trigger}
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-fg px-2 py-1 text-xs text-bg opacity-0 shadow group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {label}
      </span>
    </span>
  );
}
